import type { AxiosResponse } from 'axios';
import { apiClient } from './api-client';

export const TEMPLATE_CATEGORIES = [
  'AUTHENTICATION',
  'INSPECTION',
  'PROCEDURE',
  'AUTHORIZATION',
  'SERVICE',
  'EQUIPMENT',
  'COMMERCIAL',
  'CUSTOMER',
  'SYSTEM',
] as const;

export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];
export type TemplateStatus = 'DRAFT' | 'ACTIVE' | 'DISABLED';

export interface EmailTemplate {
  id: string;
  tenantId: string;
  masterTemplateId: string | null;
  isOverride: boolean;
  templateKey: string;
  name: string;
  category: string;
  description: string | null;
  channelType: string;
  subject: string;
  htmlBody: string;
  plainTextBody: string;
  status: string;
  isArchived: boolean;
  currentVersion: number;
  /** True while an unpublished draft is open on this template. */
  hasUnpublishedChanges: boolean;
  /** Draft content — null when no draft is open. */
  draftSubject: string | null;
  draftHtmlBody: string | null;
  draftPlainTextBody: string | null;
  assignedGatewayId: string | null;
  createdById: string | null;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailTemplateVersion {
  id: string;
  templateId: string;
  versionNumber: number;
  subject: string;
  htmlBody: string;
  plainTextBody: string;
  /** Summary of changes, captured at publish time. */
  changeNote: string | null;
  /** Release notes, captured at publish time. */
  releaseNotes: string | null;
  changedByName: string | null;
  changedByRole: string | null;
  createdAt: string;
}

export interface VariableDefinition {
  key: string;
  label: string;
  module: string;
  category: string[];
  exampleValue: string;
  description?: string;
}

export interface TemplateListParams {
  search?: string;
  status?: string;
  category?: string;
  channelType?: string;
  isArchived?: boolean;
  gatewayId?: string;
}

export interface CreateTemplatePayload {
  name: string;
  templateKey: string;
  category: string;
  description?: string;
  channelType?: string;
  subject: string;
  htmlBody: string;
  plainTextBody: string;
  status?: string;
  assignedGatewayId?: string;
  masterTemplateId?: string;
}

export interface UpdateTemplatePayload {
  name?: string;
  category?: string;
  description?: string | null;
  subject?: string;
  htmlBody?: string;
  plainTextBody?: string;
  assignedGatewayId?: string | null;
}

/** Publishing is the only action that creates a version — both fields required. */
export interface PublishVersionPayload {
  changeSummary: string;
  releaseNotes: string;
}

export interface SendTestEmailPayload {
  recipientEmail: string;
  /** Send the open draft instead of the live published content. */
  useDraft?: boolean;
}

export interface SendTestEmailResult {
  success: boolean;
  message: string;
  gatewayName: string;
  gatewayProvider: string;
  resolvedFrom: string;
  sentContent: 'DRAFT' | 'PUBLISHED';
  recipientEmail: string;
  sentAt: string;
}

export interface PreviewRequest {
  context?: Record<string, string>;
}

export interface PreviewResponse {
  subject: string;
  htmlBody: string;
  plainTextBody: string;
  templateKey: string;
  resolvedFrom: 'TENANT' | 'MASTER';
  gatewayId: string | null;
  unresolvedVariables: string[];
}

const BASE = '/email-templates';

export const communicationTemplateService = {
  list: (params: TemplateListParams = {}): Promise<EmailTemplate[]> =>
    apiClient
      .get<EmailTemplate[]>(BASE, { params })
      .then((r: AxiosResponse<EmailTemplate[]>) => r.data),

  getVariables: (): Promise<VariableDefinition[]> =>
    apiClient
      .get<VariableDefinition[]>(`${BASE}/variables`)
      .then((r: AxiosResponse<VariableDefinition[]>) => r.data),

  get: (id: string): Promise<EmailTemplate> =>
    apiClient.get<EmailTemplate>(`${BASE}/${id}`).then((r: AxiosResponse<EmailTemplate>) => r.data),

  getVersions: (id: string): Promise<EmailTemplateVersion[]> =>
    apiClient
      .get<EmailTemplateVersion[]>(`${BASE}/${id}/versions`)
      .then((r: AxiosResponse<EmailTemplateVersion[]>) => r.data),

  create: (payload: CreateTemplatePayload): Promise<EmailTemplate> =>
    apiClient.post<EmailTemplate>(BASE, payload).then((r: AxiosResponse<EmailTemplate>) => r.data),

  update: (id: string, payload: UpdateTemplatePayload): Promise<EmailTemplate> =>
    apiClient
      .patch<EmailTemplate>(`${BASE}/${id}`, payload)
      .then((r: AxiosResponse<EmailTemplate>) => r.data),

  /** Promotes the open draft to a new version. Requires summary + release notes. */
  publish: (id: string, payload: PublishVersionPayload): Promise<EmailTemplate> =>
    apiClient
      .post<EmailTemplate>(`${BASE}/${id}/publish`, payload)
      .then((r: AxiosResponse<EmailTemplate>) => r.data),

  discardDraft: (id: string): Promise<EmailTemplate> =>
    apiClient
      .patch<EmailTemplate>(`${BASE}/${id}/discard-draft`)
      .then((r: AxiosResponse<EmailTemplate>) => r.data),

  sendTest: (id: string, payload: SendTestEmailPayload): Promise<SendTestEmailResult> =>
    apiClient
      .post<SendTestEmailResult>(`${BASE}/${id}/send-test`, payload)
      .then((r: AxiosResponse<SendTestEmailResult>) => r.data),

  activate: (id: string): Promise<EmailTemplate> =>
    apiClient
      .patch<EmailTemplate>(`${BASE}/${id}/activate`)
      .then((r: AxiosResponse<EmailTemplate>) => r.data),

  disable: (id: string): Promise<EmailTemplate> =>
    apiClient
      .patch<EmailTemplate>(`${BASE}/${id}/disable`)
      .then((r: AxiosResponse<EmailTemplate>) => r.data),

  archive: (id: string): Promise<EmailTemplate> =>
    apiClient
      .patch<EmailTemplate>(`${BASE}/${id}/archive`)
      .then((r: AxiosResponse<EmailTemplate>) => r.data),

  restore: (id: string): Promise<EmailTemplate> =>
    apiClient
      .patch<EmailTemplate>(`${BASE}/${id}/restore`)
      .then((r: AxiosResponse<EmailTemplate>) => r.data),

  rollback: (id: string, version: number): Promise<EmailTemplate> =>
    apiClient
      .patch<EmailTemplate>(`${BASE}/${id}/rollback/${version}`)
      .then((r: AxiosResponse<EmailTemplate>) => r.data),

  preview: (id: string, payload: PreviewRequest = {}): Promise<PreviewResponse> =>
    apiClient
      .post<PreviewResponse>(`${BASE}/${id}/preview`, payload)
      .then((r: AxiosResponse<PreviewResponse>) => r.data),
};
